import { useState } from "react";
import { X, Plus, Trash2, Calendar, DollarSign, Users, MapPin, FileText, Award, Shield, Save } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

interface ProductionReadinessFormProps {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: any;
}

export const ProductionReadinessForm = ({ projectId, onClose, onSuccess, initialData }: ProductionReadinessFormProps) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"plan" | "verification">("plan");
  const [saving, setSaving] = useState(false);

  // Production Plan State
  const [shootDates, setShootDates] = useState<Array<{ date: string; location: string }>>(
    initialData?.shoot_dates || []
  );
  const [releaseDates, setReleaseDates] = useState<Array<{ date: string; platform: string }>>(
    initialData?.release_dates || []
  );
  const [budgetFunded, setBudgetFunded] = useState(initialData?.budget_funded_amount || "");
  const [fundingSources, setFundingSources] = useState<string[]>(initialData?.funding_sources || []);
  const [teamMembers, setTeamMembers] = useState<Array<{ name: string; role: string; bio: string }>>(
    initialData?.team_members || []
  );
  const [locations, setLocations] = useState<Array<{ name: string; details: string }>>(
    initialData?.locations || []
  );
  const [equipment, setEquipment] = useState<string[]>(initialData?.equipment_list || []);
  const [permits, setPermits] = useState<Array<{ type: string; status: string; details: string }>>(
    initialData?.permits || []
  );

  // Verification State
  const [brandWork, setBrandWork] = useState<Array<{ brand: string; project: string; year: string; videoUrl: string }>>(
    initialData?.brand_work_portfolio || []
  );
  const [credits, setCredits] = useState<Array<{ title: string; role: string; year: string; platform: string; sampleUrl: string }>>(
    initialData?.production_credits || []
  );
  const [insurance, setInsurance] = useState({
    hasCoverage: initialData?.insurance_coverage?.has_coverage || false,
    type: initialData?.insurance_coverage?.type || "",
    provider: initialData?.insurance_coverage?.provider || "",
    proofUrl: initialData?.insurance_coverage?.proof_url || "",
  });

  const handleSaveProductionPlan = async () => {
    setSaving(true);
    try {
      await apiClient.updateProductionPlan(projectId, {
        shoot_dates: shootDates,
        release_dates: releaseDates,
        budget_funded_amount: budgetFunded ? parseFloat(budgetFunded) : null,
        funding_sources: fundingSources,
        team_members: teamMembers,
        locations: locations,
        equipment_list: equipment,
        permits: permits,
      });

      toast({
        title: "Success",
        description: "Production plan updated successfully",
      });
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update production plan",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVerification = async () => {
    setSaving(true);
    try {
      await apiClient.updateVerification(projectId, {
        brand_work_portfolio: brandWork,
        production_credits: credits,
        insurance_coverage: insurance.hasCoverage ? insurance : null,
      });

      toast({
        title: "Success",
        description: "Verification data updated successfully",
      });
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update verification data",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-5xl my-8 shadow-2xl border border-gray-200 dark:border-gray-800 animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Complete Production Readiness</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Add details to improve your project's attractiveness to brands</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-white/50 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={() => setActiveTab("plan")}
            className={`flex-1 px-6 py-4 text-sm font-medium border-b-2 transition-all ${
              activeTab === "plan"
                ? "border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-900"
                : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Production Plan
          </button>
          <button
            onClick={() => setActiveTab("verification")}
            className={`flex-1 px-6 py-4 text-sm font-medium border-b-2 transition-all ${
              activeTab === "verification"
                ? "border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-900"
                : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Verification
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === "plan" ? (
            <div className="space-y-8">
              {/* Timeline Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Timeline</h3>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">Shoot Dates</label>
                  {shootDates.map((date, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="date"
                        value={date.date}
                        onChange={(e) => {
                          const newDates = [...shootDates];
                          newDates[idx].date = e.target.value;
                          setShootDates(newDates);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Location"
                        value={date.location}
                        onChange={(e) => {
                          const newDates = [...shootDates];
                          newDates[idx].location = e.target.value;
                          setShootDates(newDates);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        onClick={() => setShootDates(shootDates.filter((_, i) => i !== idx))}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setShootDates([...shootDates, { date: "", location: "" }])}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Shoot Date
                  </button>
                </div>
              </div>

              {/* Budget Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Budget Status</h3>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">Amount Funded</label>
                  <input
                    type="number"
                    value={budgetFunded}
                    onChange={(e) => setBudgetFunded(e.target.value)}
                    placeholder="Enter funded amount"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Team Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Below-the-Line Team</h3>
                </div>
                {teamMembers.map((member, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Name"
                        value={member.name}
                        onChange={(e) => {
                          const newMembers = [...teamMembers];
                          newMembers[idx].name = e.target.value;
                          setTeamMembers(newMembers);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Role"
                        value={member.role}
                        onChange={(e) => {
                          const newMembers = [...teamMembers];
                          newMembers[idx].role = e.target.value;
                          setTeamMembers(newMembers);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <textarea
                      placeholder="Bio"
                      value={member.bio}
                      onChange={(e) => {
                        const newMembers = [...teamMembers];
                        newMembers[idx].bio = e.target.value;
                        setTeamMembers(newMembers);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={2}
                    />
                    <button
                      onClick={() => setTeamMembers(teamMembers.filter((_, i) => i !== idx))}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setTeamMembers([...teamMembers, { name: "", role: "", bio: "" }])}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Team Member
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Past Brand Work */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Past Brand Work</h3>
                </div>
                {brandWork.map((work, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Brand Name"
                        value={work.brand}
                        onChange={(e) => {
                          const newWork = [...brandWork];
                          newWork[idx].brand = e.target.value;
                          setBrandWork(newWork);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Project Name"
                        value={work.project}
                        onChange={(e) => {
                          const newWork = [...brandWork];
                          newWork[idx].project = e.target.value;
                          setBrandWork(newWork);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Year"
                        value={work.year}
                        onChange={(e) => {
                          const newWork = [...brandWork];
                          newWork[idx].year = e.target.value;
                          setBrandWork(newWork);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="url"
                        placeholder="Video URL"
                        value={work.videoUrl}
                        onChange={(e) => {
                          const newWork = [...brandWork];
                          newWork[idx].videoUrl = e.target.value;
                          setBrandWork(newWork);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => setBrandWork(brandWork.filter((_, i) => i !== idx))}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setBrandWork([...brandWork, { brand: "", project: "", year: "", videoUrl: "" }])}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Brand Work
                </button>
              </div>

              {/* Production Credits */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-5 h-5 text-yellow-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Production Credits</h3>
                </div>
                {credits.map((credit, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Project Title"
                        value={credit.title}
                        onChange={(e) => {
                          const newCredits = [...credits];
                          newCredits[idx].title = e.target.value;
                          setCredits(newCredits);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Your Role"
                        value={credit.role}
                        onChange={(e) => {
                          const newCredits = [...credits];
                          newCredits[idx].role = e.target.value;
                          setCredits(newCredits);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Year"
                        value={credit.year}
                        onChange={(e) => {
                          const newCredits = [...credits];
                          newCredits[idx].year = e.target.value;
                          setCredits(newCredits);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Platform"
                        value={credit.platform}
                        onChange={(e) => {
                          const newCredits = [...credits];
                          newCredits[idx].platform = e.target.value;
                          setCredits(newCredits);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="url"
                        placeholder="Sample URL"
                        value={credit.sampleUrl}
                        onChange={(e) => {
                          const newCredits = [...credits];
                          newCredits[idx].sampleUrl = e.target.value;
                          setCredits(newCredits);
                        }}
                        className="col-span-2 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => setCredits(credits.filter((_, i) => i !== idx))}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setCredits([...credits, { title: "", role: "", year: "", platform: "", sampleUrl: "" }])}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Credit
                </button>
              </div>

              {/* Insurance */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Insurance Coverage</h3>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={insurance.hasCoverage}
                      onChange={(e) => setInsurance({ ...insurance, hasCoverage: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">I have production insurance</span>
                  </label>
                  {insurance.hasCoverage && (
                    <>
                      <input
                        type="text"
                        placeholder="Insurance Type"
                        value={insurance.type}
                        onChange={(e) => setInsurance({ ...insurance, type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Provider"
                        value={insurance.provider}
                        onChange={(e) => setInsurance({ ...insurance, provider: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="url"
                        placeholder="Proof URL (certificate, policy document)"
                        value={insurance.proofUrl}
                        onChange={(e) => setInsurance({ ...insurance, proofUrl: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={activeTab === "plan" ? handleSaveProductionPlan : handleSaveVerification}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};